import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import Share from '../share';
import CardTextSearch from './card-textsearch';
import { getConfig, getTranslation } from '../../utils/config';
import Pin from '../pin';
import humanFile from '../../utils/human-file';
import { PAGE_VARIANT } from '../../constants';
import { findGetParameter } from '../../utils/parameters';
import type { IItem } from '../../types';

type MobileCardViewProps = {
  id: number;
  taggingProduct: Array<string>;
  text: string;
  childOrder: Array<number>;
  loginRequired: boolean;
  cardData: Array<IItem | undefined>;
  groupAttributes: Array<{ facetname: string; displayname: string }>;
  handleDownload: (e: MouseEvent<HTMLAnchorElement>) => void;
  variant?: string;
  dfSubtitle?: string;
  removeItem?: (item: number) => void;
};

export default function MobileCardView({
  id,
  taggingProduct,
  text,
  loginRequired,
  cardData,
  groupAttributes,
  handleDownload,
  variant = PAGE_VARIANT.RESULT,
  dfSubtitle,
  removeItem,
}: MobileCardViewProps): React.ReactElement {
  const [moreInfo, toggleMoreInfo] = useState<boolean>(false);
  const [clampText, setClampText] = useState(!!text);
  const textRef = useRef<HTMLDivElement>(null);
  const domain = getConfig('download_url_prefix', '');
  const cmsNativeLanguage = getConfig('cms_native_language', 'EN-EN')?.toLocaleLowerCase();
  const screenwidth = useRef(window.innerWidth);

  const shareItem = findGetParameter('download_item');
  const shareItemRef = useRef<HTMLDivElement>(null);

  const handleResize = useDebouncedCallback(() => {
    if (textRef.current) {
      setClampText(textRef.current.offsetHeight < textRef.current.scrollHeight);
    }
  }, 300);

  const handleTextClamp = useCallback(() => {
    // avoid resize event on mobile devices on scroll
    if (screenwidth.current !== window.innerWidth) {
      // close more info text before checking offsetHeight
      toggleMoreInfo(false);
      handleResize();
      screenwidth.current = window.innerWidth;
    }
  }, [handleResize]);

  useEffect(() => {
    if (textRef.current) {
      setClampText(textRef.current.offsetHeight < textRef.current.scrollHeight);
      const textRefParent = textRef.current?.closest('.df-product-downloads__group');

      const observer = new MutationObserver(function (event) {
        if (
          event[0].type === 'attributes' &&
          (event[0].target as Element)?.getAttribute('aria-expanded') === 'true' &&
          textRefParent
        ) {
          if (textRef.current) {
            setClampText(textRef.current.offsetHeight < textRef.current.scrollHeight);
            observer.disconnect();
          }
        }
      });

      if (textRefParent) {
        observer.observe(textRefParent, {
          attributes: true,
          attributeFilter: ['class'],
          childList: false,
          characterData: false,
        });
      }
    }
  }, [textRef.current?.offsetHeight]);

  useEffect(() => {
    if (textRef.current) {
      setClampText(textRef.current.offsetHeight < textRef.current.scrollHeight);
    }
    window.addEventListener('resize', handleTextClamp);

    return () => {
      window.removeEventListener('resize', handleTextClamp);
    };
  }, [handleTextClamp, textRef]);

  useEffect(() => {
    if (shareItemRef.current) {
      shareItemRef.current.scrollIntoView();
    }
  }, [shareItemRef]);

  return (
    <>
      {loginRequired && (
        <span className="df-list-module__login-required">{getTranslation('login_required')}</span>
      )}
      {dfSubtitle && (
        <span className="df-list-module__note" dangerouslySetInnerHTML={{ __html: dfSubtitle }} />
      )}
      {text && (
        <>
          <div
            className={`df-text df-teaser-text ${moreInfo ? '' : 'df-teaser-text--minimized'}`}
            dangerouslySetInnerHTML={{ __html: text }}
            ref={textRef}
          />
          {clampText && (
            <button
              className="df-show-hide-details__button df-show-hide-details__button--chevron collapsed"
              aria-expanded={moreInfo}
              type="button"
              onClick={() => toggleMoreInfo(!moreInfo)}
            >
              {getTranslation(moreInfo ? 'less_info' : 'more_info')}
            </button>
          )}
        </>
      )}
      {!!cardData.length && (
        <div className="df-list-module__mobile-files">
          {(cardData.filter(Boolean) as IItem[]).map((file) => {
            const downloadLink = `${domain}/${cmsNativeLanguage}/download/${file.pimId}`;

            return (
              <div
                key={file?.pimId}
                className={`df-list-module__mobile-files__file ${
                  String(file?.pimId) === shareItem ? 'df-shared-bookmark' : ''
                }`}
                ref={String(file?.pimId) === shareItem ? shareItemRef : null}
              >
                {groupAttributes?.map((attribute) => (
                  <div className="df-list-module__mobile-files__attr" key={attribute.facetname}>
                    <span>{attribute.displayname}</span>
                    <span>{file?.[attribute.facetname].join(', ')}</span>
                  </div>
                ))}
                <div className="df-list-module__mobile-files__actions">
                  <div className="df-left-actions">
                    <a
                      href={downloadLink}
                      target="_blank"
                      onClick={handleDownload}
                      className={`df-download-link  ${
                        loginRequired ? 'df-download-link__login-required' : ''
                      }`}
                      rel="noreferrer"
                    >
                      {file?.dfAttributeDfFileType}&nbsp;
                      {file?.dfAttributeDfFileSize > 0 &&
                        `(${humanFile(file.dfAttributeDfFileSize)})`}
                    </a>
                  </div>
                  <div className="df-right-actions">
                    <Share group={id} item={file?.pimId} type="item" />
                    {variant === PAGE_VARIANT.RESULT && <Pin id={file?.pimId || ''} group={id} />}
                    {variant === PAGE_VARIANT.BOOKMARK && removeItem && (
                      <button
                        className="df-icon-btn"
                        onClick={() => removeItem(file.pimId)}
                        title={getTranslation('bookmark_removed_title')}
                      >
                        <svg className="df-action-icon">
                          <use href={getConfig('material_symbols_url') + '#delete_400'} />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!!cardData.length && (
        <span className="df-list-module__mobile-amount">
          {cardData.length}{' '}
          {cardData.length > 1
            ? getTranslation('bookmark_title_downloads')
            : getTranslation('bookmark_title_download')}
        </span>
      )}
      {taggingProduct && taggingProduct.length >= 1 && (
        <div className="df-list-module__mobile-search row flex">
          <div className="col col-12">
            <CardTextSearch taggingProduct={taggingProduct} groupId={id} />
          </div>
        </div>
      )}
    </>
  );
}
